import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Save, User, Upload } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getImageSrc(url) {
  if (!url) return '';
  if (url.startsWith('/api/')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
  return url;
}

export default function ProfilePage() {
  const [content, setContent] = useState({
    bio_name: '',
    bio_title: '',
    bio_text: '',
    bio_image: '',
    payment_email: '',
    whatsapp_number: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const { data } = await axios.get(`${API}/content`, { withCredentials: true });
      setContent(data);
    } catch (error) {
      toast.error('Failed to fetch content');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPEG, PNG, GIF, and WEBP images are allowed');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 10MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const { data } = await axios.post(`${API}/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setContent({ ...content, bio_image: data.url });
      toast.success('Profile photo uploaded!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${API}/content`, content, { withCredentials: true });
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9C6A48]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="profile-page">
      <div className="mb-8">
        <h1 className="text-3xl font-light text-[#2B2623]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Profile & Bio
        </h1>
        <p className="text-[#756A63] mt-1">Update your bio and contact information displayed on the website</p>
      </div>

      <div className="max-w-2xl">
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Profile Photo Section */}
          <div className="bg-white rounded-xl border border-[#E3DACD] p-6">
            <h2 className="text-lg font-medium text-[#2B2623] mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-[#9C6A48]" />
              Profile Photo
            </h2>
            <div className="flex items-start gap-6">
              <div className="shrink-0">
                {content.bio_image ? (
                  <div className="w-32 h-32 rounded-2xl overflow-hidden border border-[#E3DACD]">
                    <img
                      src={getImageSrc(content.bio_image)}
                      alt="Profile"
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.src = 'https://via.placeholder.com/200x200?text=Photo';
                      }}
                    />
                  </div>
                ) : (
                  <div className="w-32 h-32 rounded-2xl bg-[#EDE7DD] flex items-center justify-center">
                    <User className="w-12 h-12 text-[#9C6A48]" />
                  </div>
                )}
              </div>
              <div className="flex-1 space-y-3">
                <div>
                  <Button
                    type="button"
                    variant="outline"
                    className="border-[#E3DACD] hover:bg-[#9C6A48]/5"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    data-testid="upload-profile-photo-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploading ? 'Uploading...' : 'Upload Photo'}
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp"
                    className="hidden"
                    onChange={handleImageUpload}
                    data-testid="profile-photo-input"
                  />
                  <p className="text-xs text-[#756A63] mt-2">JPEG, PNG, GIF, WEBP up to 10MB</p>
                </div>
                <div>
                  <label className="text-xs text-[#756A63] mb-1 block">Or paste image URL:</label>
                  <Input
                    data-testid="bio-image-input"
                    value={content.bio_image}
                    onChange={(e) => setContent({ ...content, bio_image: e.target.value })}
                    placeholder="https://example.com/your-photo.jpg"
                    className="border-[#E3DACD] text-sm"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Bio Section */}
          <div className="bg-white rounded-xl border border-[#E3DACD] p-6">
            <h2 className="text-lg font-medium text-[#2B2623] mb-4">About Section</h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-[#2B2623] mb-2 block">Your Name</label>
                <Input
                  data-testid="bio-name-input"
                  value={content.bio_name}
                  onChange={(e) => setContent({ ...content, bio_name: e.target.value })}
                  placeholder="Abigail Ansah"
                  className="border-[#E3DACD]"
                />
                <p className="text-xs text-[#756A63] mt-1">Displayed below your photo on the About section</p>
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2623] mb-2 block">Section Title</label>
                <Input
                  data-testid="bio-title-input"
                  value={content.bio_title}
                  onChange={(e) => setContent({ ...content, bio_title: e.target.value })}
                  placeholder="Meet Abby"
                  className="border-[#E3DACD]"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2623] mb-2 block">Bio Text</label>
                <Textarea
                  data-testid="bio-text-input"
                  value={content.bio_text}
                  onChange={(e) => setContent({ ...content, bio_text: e.target.value })}
                  placeholder="Write about yourself..."
                  rows={6}
                  className="border-[#E3DACD]"
                />
              </div>
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-white rounded-xl border border-[#E3DACD] p-6">
            <h2 className="text-lg font-medium text-[#2B2623] mb-4">Contact Information</h2>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-[#2B2623] mb-2 block">Payment Email (e-Transfer)</label>
                <Input
                  data-testid="payment-email-input"
                  type="email"
                  value={content.payment_email}
                  onChange={(e) => setContent({ ...content, payment_email: e.target.value })}
                  placeholder="your@email.com"
                  className="border-[#E3DACD]"
                />
                <p className="text-xs text-[#756A63] mt-1">
                  This email will be shown to customers for Interac e-Transfer payments
                </p>
              </div>
              <div>
                <label className="text-sm font-medium text-[#2B2623] mb-2 block">WhatsApp Number</label>
                <Input
                  data-testid="whatsapp-number-input"
                  value={content.whatsapp_number}
                  onChange={(e) => setContent({ ...content, whatsapp_number: e.target.value })}
                  placeholder="905-965-4909"
                  className="border-[#E3DACD]"
                />
                <p className="text-xs text-[#756A63] mt-1">
                  Used for the WhatsApp button on the website
                </p>
              </div>
            </div>
          </div>

          <Button
            type="submit"
            disabled={saving}
            className="bg-[#9C6A48] hover:bg-[#8B5A33] text-white px-8"
            data-testid="save-profile-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </form>
      </div>
    </div>
  );
}
